/**
 * The Document routing screen's data: one row per fax type, saved one at a time.
 *
 * A failed save or reset leaves the row as it was and says so in a toast.
 */
import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../../../store';
import {
  getFaxRouteSettings,
  resetFaxRouteOverride,
  saveFaxRouteOverride,
  type FaxRouteOverrideBody,
  type FaxRouteSettingRow,
  type MiscTaskTypeId,
} from '../../../services/faxRouteSettingsApi';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';

const SAVE_FAILED = 'Could not save the routing.';

export function useFaxRouteSettings() {
  const showError = useStore(s => s.showError);
  const [rows, setRows] = useState<FaxRouteSettingRow[]>([]);
  const [miscTaskTypes, setMiscTaskTypes] = useState<MiscTaskTypeId[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getFaxRouteSettings()
      .then(found => {
        if (cancelled) return;
        setRows(found.rows);
        setMiscTaskTypes(found.misc_task_types);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load fax routing', error);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt(n => n + 1), []);

  const save = useCallback(
    async (faxTypeId: string, body: FaxRouteOverrideBody) => {
      try {
        const row = await saveFaxRouteOverride(faxTypeId, body);
        setRows(prev => prev.map(r => (r.fax_type_id === faxTypeId ? row : r)));
        return true;
      } catch (error) {
        console.error('Failed to save fax routing', error);
        showError(getBusinessErrorMessage(error) ?? SAVE_FAILED);
        return false;
      }
    },
    [showError]
  );

  const reset = useCallback(
    async (faxTypeId: string) => {
      try {
        const row = await resetFaxRouteOverride(faxTypeId);
        // A retired type is listed only while it has an override.
        const dropped = row.retired && !row.override;
        setRows(prev =>
          dropped
            ? prev.filter(r => r.fax_type_id !== faxTypeId)
            : prev.map(r => (r.fax_type_id === faxTypeId ? row : r))
        );
        return true;
      } catch (error) {
        console.error('Failed to reset fax routing', error);
        showError(getBusinessErrorMessage(error) ?? SAVE_FAILED);
        return false;
      }
    },
    [showError]
  );

  return { rows, miscTaskTypes, loading, failed, reload, save, reset };
}
